import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface HabitLoop {
  name: string;
  english: string;
  desc: string;
  icon: string;
  color: string;
}

interface FormationStage {
  stage: string;
  english: string;
  desc: string;
}

interface HabitStrategy {
  name: string;
  desc: string;
  icon: string;
}

interface HabitFormationTemplateProps {
  topic: string;
  subtitle: string;
  habitLoop: HabitLoop[];
  stages: FormationStage[];
  strategies: HabitStrategy[];
  dayMilestones: { day: number; label: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: HabitFormationTemplateProps = {
  topic: '习惯养成科学',
  subtitle: 'The Science of Habit Formation',
  habitLoop: [
    {
      name: '暗示',
      english: 'Cue',
      desc: '触发行为的环境信号或内部状态',
      icon: '🔔',
      color: '#a3e635',
    },
    {
      name: '渴求',
      english: 'Craving',
      desc: '对状态改变的内在驱动力',
      icon: '💫',
      color: '#fbbf24',
    },
    {
      name: '反应',
      english: 'Response',
      desc: '你实际执行的习惯行为',
      icon: '⚡',
      color: '#38bdf8',
    },
    {
      name: '奖赏',
      english: 'Reward',
      desc: '满足渴求并强化行为回路',
      icon: '🏆',
      color: '#f472b6',
    },
  ],
  stages: [
    {
      stage: '不自觉无能',
      english: 'Unconscious Incompetence',
      desc: '不知道自己不知道',
    },
    {
      stage: '自觉无能',
      english: 'Conscious Incompetence',
      desc: '意识到自己的不足',
    },
    {
      stage: '自觉有能',
      english: 'Conscious Competence',
      desc: '刻意练习可以做到',
    },
    {
      stage: '不自觉有能',
      english: 'Unconscious Competence',
      desc: '习惯已完全自动化',
    },
  ],
  strategies: [
    { name: '习惯叠加', desc: '把新习惯附加到已有习惯之后', icon: '🧩' },
    { name: '环境设计', desc: '调整环境使好习惯更容易执行', icon: '🏠' },
    { name: '最小行动', desc: '从两分钟版本开始降低门槛', icon: '🎯' },
    { name: '奖赏替换', desc: '用健康奖赏替代不良行为奖赏', icon: '🔄' },
  ],
  dayMilestones: [
    { day: 21, label: '初步习惯' },
    { day: 42, label: '巩固期' },
    { day: 66, label: '自动化' },
    { day: 90, label: '身份转变' },
  ],
  stats: [
    { value: '66天', label: '平均形成习惯所需时间' },
    { value: '43%', label: '日常行为由习惯驱动' },
    { value: '2分钟', label: '最小行动法则起始时长' },
  ],
  bottomQuote:
    '你不会上升到目标的高度，你只会下降到系统的水平——微小习惯的复利效应造就非凡人生',
};

export const HabitFormationTemplate: React.FC<
  Partial<HabitFormationTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const loopStart = 30;
  const stageStart = 160;
  const ringStart = 200;
  const strategyStart = 350;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const ringProgress = interpolate(frame, [ringStart, ringStart + 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0f1a0a 0%, #0a1608 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating chain link particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 389) % 1920;
        const y = (i * 211) % 1080;
        const size = 10 + (i % 6) * 2;
        const drift = Math.sin(frame * 0.025 + i * 0.9) * 8;
        const driftX = Math.cos(frame * 0.02 + i * 0.7) * 5;
        const pulse = 0.06 + 0.04 * Math.sin(frame * 0.04 + i * 0.8);
        const colors = ['#a3e635', '#84cc16', '#65a30d', '#4d7c0f'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size * 0.6,
              borderRadius: size * 0.3,
              border: `1.5px solid ${colors[i % colors.length]}`,
              opacity: pulse,
              boxShadow: `0 0 ${size}px ${colors[i % colors.length]}20`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#4d7c0f',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#a3e635',
            flexShrink: 0,
          }}
        >
          🔗 行为科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ecfccb',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Habit Loop ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 460 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          习惯回路 (Habit Loop)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.habitLoop.map((h, i) => {
            const s = spring({
              frame: frame - (loopStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    background: '#0f1a0a',
                    borderRadius: 14,
                    padding: '12px 16px',
                    border: `1.5px solid ${h.color}30`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 30}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: `${h.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      flexShrink: 0,
                      boxShadow: `0 0 18px ${h.color}30`,
                    }}
                  >
                    {h.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
                    >
                      <span
                        style={{ fontSize: 22, fontWeight: 800, color: h.color }}
                      >
                        {h.name}
                      </span>
                      <span style={{ fontSize: 22, color: '#64748b' }}>
                        {h.english}
                      </span>
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {h.desc}
                    </div>
                  </div>
                </div>
                {i < p.habitLoop.length - 1 && (
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#4d7c0f',
                      fontSize: 18,
                      opacity: s,
                    }}
                  >
                    ↓
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Formation Stages */}
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            习惯养成四阶段
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {p.stages.map((st, i) => {
              const s = spring({
                frame: frame - (stageStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <React.Fragment key={i}>
                  <div
                    style={{
                      flex: 1,
                      background: '#0f1a0a',
                      borderRadius: 12,
                      padding: '10px 2px',
                      textAlign: 'center',
                      border: '1.5px solid #4d7c0f40',
                      opacity: s,
                      transform: `translateY(${(1 - s) * 16}px)`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        color: '#a3e635',
                        fontWeight: 700,
                        marginBottom: 3,
                      }}
                    >
                      阶段 {i + 1}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: '#ecfccb',
                        marginBottom: 3,
                      }}
                    >
                      {st.stage}
                    </div>
                    <div style={{ fontSize: 16, color: '#94a3b8' }}>{st.desc}</div>
                  </div>
                  {i < p.stages.length - 1 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#a3e635',
                        fontSize: 16,
                        opacity: s,
                      }}
                    >
                      →
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: 21-66 Day Progress Ring SVG ===== */}
      <div style={{ position: 'absolute', top: 120, left: 600, width: 680 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          习惯养成进度环 · 21→66→90天
        </div>
        <svg width="660" height="620" viewBox="0 -25 660 585">
          <defs>
            <linearGradient id="habitRingGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a3e635" />
              <stop offset="50%" stopColor="#65a30d" />
              <stop offset="100%" stopColor="#4d7c0f" />
            </linearGradient>
          </defs>

          {/* Background ring */}
          <circle cx="330" cy="245" r="180" fill="none" stroke="#1a2e0a" strokeWidth="30" />

          {/* Progress ring */}
          <circle
            cx="330"
            cy="245"
            r="180"
            fill="none"
            stroke="url(#habitRingGrad)"
            strokeWidth="30"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 180}
            strokeDashoffset={2 * Math.PI * 180 * (1 - ringProgress)}
            transform="rotate(-90 330 245)"
          />

          {/* Inner ring */}
          <circle cx="330" cy="245" r="145" fill="none" stroke="#4d7c0f20" strokeWidth="2.5" />

          {/* Center text */}
          <text x="330" y="232" fill="#a3e635" fontSize="60" fontWeight="900" textAnchor="middle">
            {Math.round(ringProgress * 90)}
          </text>
          <text x="330" y="268" fill="#94a3b8" fontSize="24" textAnchor="middle">
            / 90 天
          </text>
          <text x="330" y="295" fill="#64748b" fontSize="18" textAnchor="middle">
            习惯养成进度
          </text>

          {/* Milestone markers on the ring */}
          {p.dayMilestones.map((m, i) => {
            const angle = (m.day / 90) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const cx = 330 + 180 * Math.cos(rad);
            const cy = 245 + 180 * Math.sin(rad);
            const labelX = 330 + 250 * Math.cos(rad);
            const labelY = 245 + 250 * Math.sin(rad);
            const dotS = spring({
              frame: frame - (ringStart + 40 + i * 20),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={dotS}>
                <circle cx={cx} cy={cy} r="8" fill="#ecfccb" stroke="#a3e635" strokeWidth="2.5" />
                <text
                  x={labelX}
                  y={labelY - 10}
                  fill="#a3e635"
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {m.day}天
                </text>
                <text
                  x={labelX}
                  y={labelY + 14}
                  fill="#94a3b8"
                  fontSize="18"
                  textAnchor="middle"
                >
                  {m.label}
                </text>
              </g>
            );
          })}

          {/* Compound effect curve */}
          <g
            opacity={interpolate(frame, [ringStart + 80, ringStart + 110], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            <text x="330" y="540" fill="#94a3b8" fontSize="22" textAnchor="middle">
              复利效应：每天进步 1% → 一年后 37.78 倍提升
            </text>
            <text x="330" y="575" fill="#64748b" fontSize="22" textAnchor="middle">
              1.01³⁶⁵ = 37.78 ｜ 0.99³⁶⁵ = 0.03
            </text>
          </g>
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Key Strategies ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          核心策略
        </div>
        {p.strategies.map((st, i) => {
          const s = spring({
            frame: frame - (strategyStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f1a0a',
                borderRadius: 12,
                padding: '14px 16px',
                border: '1.5px solid #4d7c0f40',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: '#4d7c0f30',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  flexShrink: 0,
                }}
              >
                {st.icon}
              </div>
              <div>
                <div style={{ fontSize: 21, fontWeight: 800, color: '#a3e635' }}>
                  {st.name}
                </div>
                <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 2 }}>
                  {st.desc}
                </div>
              </div>
            </div>
          );
        })}

        {/* Habit Change Tips */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            行为改变定律
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[
              { label: '让它显而易见', icon: '👁️' },
              { label: '让它有吸引力', icon: '✨' },
              { label: '让它简便易行', icon: '🎯' },
              { label: '让它令人愉悦', icon: '🎉' },
            ].map((item, i) => {
              const s = spring({
                frame: frame - (strategyStart + 80 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0f1a0a',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #4d7c0f40',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 30, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#a3e635' }}>
                    {item.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f1a0a',
                borderRadius: 14,
                padding: '14px 18px',
                border: '1px solid #4d7c0f40',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 34, fontWeight: 900, color: '#a3e635' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#94a3b8' }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0f1a0a',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #4d7c0f',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🌱</span>
          <span style={{ fontSize: 20, color: '#ecfccb', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #4d7c0f, #a3e635, #ecfccb)',
        }}
      />
    </AbsoluteFill>
  );
};
